/**
 * UpcomingReleases - Browse/pre-save upcoming scheduled releases
 * Used on the home page or browse section
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Calendar, Clock, Heart, Music, Loader2, Bell } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UpcomingReleases() {
  const { token, user } = useAuth();
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preSaved, setPreSaved] = useState({});
  const [saving, setSaving] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: mount-only fetch / stable closure
  useEffect(() => { fetchReleases(); }, []);

  const fetchReleases = async () => {
    try {
      const res = await axios.get(`${API}/releases/upcoming`);
      setReleases(res.data.releases || []);
      
      // Check pre-save status for each release if logged in
      if (token) {
        const statuses = {};
        for (const rel of (res.data.releases || [])) {
          try {
            const statusRes = await axios.get(`${API}/releases/${rel.id}/pre-save-status`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            statuses[rel.id] = statusRes.data.pre_saved;
          } catch { /* ignore */ }
        }
        setPreSaved(statuses);
      }
    } catch {
      // Silently fail - this is a supplementary section
    } finally {
      setLoading(false);
    }
  };

  const handlePreSave = async (releaseId) => {
    if (!token) {
      toast.error('Log in to pre-save releases');
      return;
    }
    setSaving(releaseId);
    try {
      if (preSaved[releaseId]) {
        await axios.delete(`${API}/releases/${releaseId}/pre-save`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPreSaved(prev => ({ ...prev, [releaseId]: false }));
        toast.success('Pre-save removed');
      } else {
        await axios.post(`${API}/releases/${releaseId}/pre-save`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPreSaved(prev => ({ ...prev, [releaseId]: true }));
        toast.success("Pre-saved! You'll be notified on release day.");
      }
      fetchReleases();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSaving(null);
    }
  };

  const getCountdown = (dateStr) => {
    const diff = new Date(dateStr) - new Date();
    if (diff <= 0) return 'Releasing soon';
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    if (days > 0) return `${days}d ${hours}h`;
    return `${hours}h`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 size={20} className="animate-spin text-[#00CCFF]" />
      </div>
    );
  }

  if (releases.length === 0) return null;

  return (
    <div className="space-y-4" data-testid="upcoming-releases">
      <h3 className="text-white font-['Chakra_Petch'] text-base uppercase flex items-center gap-2">
        <Calendar size={16} className="text-purple-400" /> Upcoming Releases
      </h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {releases.map(rel => (
          <div
            key={rel.id}
            className="bg-[#0a0a0a] border border-[#1a1a1a] overflow-hidden group hover:border-purple-500/30 transition-colors"
            data-testid={`release-card-${rel.id}`}
          >
            {/* Artwork */}
            <div className="relative aspect-square bg-[#111]">
              {rel.track_artwork ? (
                <img src={rel.track_artwork} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Music size={32} className="text-[#222]" />
                </div>
              )}
              <div className="absolute top-2 right-2 bg-black/80 backdrop-blur-sm px-2 py-1 flex items-center gap-1">
                <Clock size={10} className="text-purple-400" />
                <span className="text-purple-400 font-mono text-xs">{getCountdown(rel.release_date)}</span>
              </div>
            </div>

            {/* Info */}
            <div className="p-3">
              <p className="text-white text-sm font-medium truncate">{rel.track_title}</p>
              <p className="text-[#888] text-xs truncate mt-0.5">
                {rel.artist_display_name || rel.artist_username}
              </p>
              <p className="text-[#555] font-mono text-xs mt-1">
                <Calendar size={10} className="inline mr-1" />
                {new Date(rel.release_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
              </p>
              {rel.description && (
                <p className="text-[#666] text-xs mt-1.5 line-clamp-2">{rel.description}</p>
              )}

              <div className="flex items-center justify-between mt-3">
                <span className="text-[#888] font-mono text-xs">
                  <Bell size={10} className="inline mr-1" />{rel.pre_save_count || 0} pre-saves
                </span>
                {user?.id !== rel.artist_id && (
                  <Button
                    onClick={() => handlePreSave(rel.id)}
                    disabled={saving === rel.id}
                    size="sm"
                    className={`h-7 px-3 text-xs font-mono ${
                      preSaved[rel.id]
                        ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30 hover:bg-purple-500/10'
                        : 'bg-purple-500 text-white hover:bg-purple-600'
                    }`}
                    data-testid={`pre-save-btn-${rel.id}`}
                  >
                    {saving === rel.id ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : preSaved[rel.id] ? (
                      <><Heart size={12} className="mr-1 fill-current" /> Saved</>
                    ) : (
                      <><Heart size={12} className="mr-1" /> Pre-Save</>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
